import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import ProductImageGallery from './components/ProductImageGallery';
import ProductInfo from './components/ProductInfo';
import ReviewsSection from './components/ReviewsSection';
import SimilarGifts from './components/SimilarGifts';
import GiftGuide from './components/GiftGuide';
import FrequentlyBought from './components/FrequentlyBought';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Breadcrumb from '../../components/ui/Breadcrumb';
import SocialShare from '../../components/ui/SocialShare';
import api from '../../services/api';
import { 
  generateProductSEO, 
  generateFAQStructuredData, 
  updatePageTitle,
  generateRobotsContent,
  trackSEOEvent 
} from '../../utils/seo';

const ProductDetailPage = () => {
  const { productSlug } = useParams();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('overview');
  const [isSticky, setIsSticky] = useState(false);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [seoData, setSeoData] = useState(null);
  // Format price with proper currency
  const formatPrice = (price, currency) => {
    if (!price) return '';
    
    const currencySymbols = {
      'USD': '$', 'EUR': '€', 'GBP': '£', 'JPY': '¥', 'CAD': 'C$', 'AUD': 'A$',
      'CHF': 'CHF', 'CNY': '¥', 'SEK': 'kr', 'NOK': 'kr', 'DKK': 'kr', 'PLN': 'zł',
      'CZK': 'Kč', 'HUF': 'Ft', 'RUB': '₽', 'BRL': 'R$', 'MXN': '$', 'INR': '₹',
      'KRW': '₩', 'SGD': 'S$', 'HKD': 'HK$', 'NZD': 'NZ$', 'ZAR': 'R', 'TRY': '₺'
    };

    const symbol = currencySymbols[currency] || currency || '€';
    const formattedPrice = parseFloat(price).toFixed(2);
    const symbolAfter = ['SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'HUF', 'TRY'];
    
    return symbolAfter.includes(currency) 
      ? `${formattedPrice} ${symbol}` 
      : `${symbol}${formattedPrice}`;
  };



  // Fetch product data from API
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await api.get(`/products/${productSlug}/`);
        
        const productData = response.data;
        setProduct(productData);
        
        // Generate SEO data
        const currentUrl = `${window.location.origin}${location.pathname}`;
        const seoMetadata = generateProductSEO(productData, currentUrl);
        setSeoData(seoMetadata);
        
        // Update page title dynamically
        updatePageTitle(seoMetadata.title);
        
        // Track product view
        try {
          await api.post(`/products/${productSlug}/track-view/`);
        } catch (trackError) {
          console.warn('Failed to track product view:', trackError);
        }
        
      } catch (err) {
        console.error('Error fetching product:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (productSlug) {
      fetchProduct();
    }
  }, [productSlug, location.pathname]);

  // Sticky buy button scroll handler
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const threshold = 800;
      setIsSticky(scrollPosition > threshold);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Info' },
    { id: 'reviews', label: 'Reviews', icon: 'Star' },
    { id: 'guide', label: 'Gift Guide', icon: 'Book' },
    { id: 'frequently-bought', label: 'Bundle Deals', icon: 'Package' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-8">
            {/* Detailed Specifications */}
            {product.specifications && Object.keys(product.specifications).length > 0 && (
              <div className="bg-white rounded-2xl border border-accent-100 p-6">
                <h3 className="font-heading text-xl font-semibold text-accent-800 mb-4">
                  Product Specifications
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(product.specifications).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between py-2 border-b border-accent-100 last:border-b-0">
                      <span className="text-accent-600 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                      <span className="font-medium text-accent-800">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Care Instructions */}
            <div className="bg-accent-50 rounded-2xl p-6">
              <h3 className="font-heading text-xl font-semibold text-accent-800 mb-4">
                Care Instructions
              </h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <Icon name="Droplets" size={20} className="text-primary mt-1" />
                  <div>
                    <h4 className="font-medium text-accent-800">Cleaning</h4>
                    <p className="text-sm text-accent-600">Gentle hand wash with mild soap, avoid harsh chemicals</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="Sun" size={20} className="text-primary mt-1" />
                  <div>
                    <h4 className="font-medium text-accent-800">Storage</h4>
                    <p className="text-sm text-accent-600">Store in cool, dry place away from direct sunlight</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="Shield" size={20} className="text-primary mt-1" />
                  <div>
                    <h4 className="font-medium text-accent-800">Handling</h4>
                    <p className="text-sm text-accent-600">Handle with care to preserve artisan craftsmanship</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'reviews':
        return <ReviewsSection product={product} />;
      case 'guide':
        return <GiftGuide product={product} />;
      case 'frequently-bought':
        return <FrequentlyBought currentProduct={product} />;
      default:
        return null;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-accent-600">Loading product details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-accent-800 mb-2">Product Not Found</h2>
              <p className="text-accent-600 mb-6">{error}</p>
              <Button
                variant="primary"
                onClick={() => window.history.back()}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Go Back
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No product data
  if (!product) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Icon name="Package" size={48} className="text-accent-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-accent-800 mb-2">No Product Data</h2>
              <p className="text-accent-600">Unable to load product information.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Generate FAQ structured data
  const faqData = generateFAQStructuredData(product);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{seoData?.title || `${product.name} - Perfect Gift | Giftsy`}</title>
        <meta name="description" content={seoData?.description || product.description} />
        <meta name="keywords" content={seoData?.keywords || `${product.name}, gifts, ${product.brand}`} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={seoData?.canonicalUrl || window.location.href} />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={seoData?.openGraph?.title || `${product.name} - Perfect Gift | Giftsy`} />
        <meta property="og:description" content={seoData?.openGraph?.description || product.description} />
        <meta property="og:image" content={seoData?.openGraph?.image || product.images?.[0]?.image} />
        <meta property="og:url" content={seoData?.openGraph?.url || window.location.href} />
        <meta property="og:type" content="product" />
        <meta property="og:site_name" content="Giftsy" />
        <meta property="og:locale" content="en_US" />
        
        {/* Product-specific Open Graph tags */}
        {seoData?.openGraph?.['product:price:amount'] && (
          <meta property="product:price:amount" content={seoData.openGraph['product:price:amount']} />
        )}
        {seoData?.openGraph?.['product:price:currency'] && (
          <meta property="product:price:currency" content={seoData.openGraph['product:price:currency']} />
        )}
        <meta property="product:availability" content="in stock" />
        <meta property="product:condition" content="new" />
        {product.id && <meta property="product:retailer_item_id" content={product.id} />}
        {product.brand && <meta property="product:brand" content={product.brand} />}
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@giftsy" />
        <meta name="twitter:title" content={seoData?.twitterCard?.title || `${product.name} - Perfect Gift | Giftsy`} />
        <meta name="twitter:description" content={seoData?.twitterCard?.description || product.description} />
        <meta name="twitter:image" content={seoData?.twitterCard?.image || product.images?.[0]?.image} />
        <meta name="twitter:image:alt" content={seoData?.twitterCard?.['image:alt'] || `${product.name} - Gift`} />
        
        {/* Additional SEO Meta Tags */}
        <meta name="robots" content={generateRobotsContent(product)} />
        <meta name="googlebot" content="index, follow" />
        <meta name="author" content="Giftsy" />
        <meta name="publisher" content="Giftsy" />
        
        {/* Mobile and Responsive */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="format-detection" content="telephone=no" />
        
        {/* Structured Data for SEO */}
        {seoData?.structuredData?.product && (
          <script type="application/ld+json">
            {JSON.stringify(seoData.structuredData.product)}
          </script>
        )}
        
        {/* Breadcrumb Structured Data */}
        {seoData?.structuredData?.breadcrumb && (
          <script type="application/ld+json">
            {JSON.stringify(seoData.structuredData.breadcrumb)}
          </script>
        )}
        
        {/* FAQ Structured Data */}
        {faqData && (
          <script type="application/ld+json">
            {JSON.stringify(faqData)}
          </script>
        )}
      </Helmet>

      <Header />
      
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-4">
        <Breadcrumb
          items={[
            { label: 'Home', href: '/' },
            { label: 'Gift Finder', href: '/gift-finder' },
            { 
              label: product.category_details?.name || 'Products', 
              href: `/categories/${product.category_details?.slug || 'all'}` 
            },
            { label: product.name }
          ]}
        />
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {/* Product Hero Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 mb-16">
          {/* Product Images */}
          <div className="order-2 lg:order-1">
            <ProductImageGallery product={product} />
          </div>
          
          {/* Product Info */}
          <div className="order-1 lg:order-2">
            <ProductInfo product={product} />
          </div>
        </div>

        {/* Similar Gifts Section */}
        <div className="mb-16">
          <SimilarGifts currentProduct={product} />
        </div>

        {/* Tabbed Content Section */}
        <div className="space-y-8">
          {/* Tab Navigation */}
          <div className="flex justify-center">
            <div className="bg-white rounded-2xl border border-accent-100 p-1 flex space-x-1 overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-xl text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-primary text-white shadow-warm'
                      : 'text-accent-600 hover:text-accent-800 hover:bg-accent-50'
                  }`}
                >
                  <Icon name={tab.icon} size={16} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="animate-fade-in">
            {renderTabContent()}
          </div>
        </div>
      </main>

      {/* Sticky Buy Button for Mobile */}
      {isSticky && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-accent-100 p-4 shadow-lg z-40 lg:hidden">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-lg font-bold text-accent-800">{formatPrice(product.current_price, product.price_currency)}</span>
              {product.original_price && (
                <span className="text-sm text-accent-500 line-through ml-2">{formatPrice(product.original_price, product.price_currency)}</span>
              )}
            </div>
            <Button
              variant="primary"
              iconName="ExternalLink"
              iconPosition="left"
              onClick={() => {
                if (product?.affiliate_url) {
                  // Track affiliate link click for analytics
                  try {
                    fetch(`/api/v1/products/${product.id}/track-affiliate-click/`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                    }).catch(err => console.warn('Failed to track affiliate click:', err));
                  } catch (error) {
                    console.warn('Failed to track affiliate click:', error);
                  }
                  
                  // Open in new tab to keep user on product page
                  window.open(product.affiliate_url, '_blank', 'noopener,noreferrer');
                } else {
                  console.log('No affiliate URL available');
                }
              }}
              className="shadow-warm"
            >
              Buy Now
            </Button>
          </div>
        </div>
      )}

      {/* Gift Concierge Float Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <Button
          variant="secondary"
          shape="circle"
          size="lg"
          iconName="MessageCircle"
          onClick={() => console.log('Open gift concierge')}
          className="shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
          title="Chat with Gift Expert"
        />
      </div>

      {/* Footer */}
      <footer className="bg-accent-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-secondary-400 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-lg">G</span>
                </div>
                <div>
                  <h3 className="font-heading font-bold text-xl">Giftsy</h3>
                  <p className="font-accent text-sm text-secondary-300">Find the perfect gift</p>
                </div>
              </div>
              <p className="text-accent-300 mb-4 max-w-md">
                Discover thoughtful gifts that create lasting memories and bring joy to every occasion.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-accent-300">
                <li><a href="/gift-finder" className="hover:text-white transition-colors">Gift Finder</a></li>
                <li><a href="/wishlists" className="hover:text-white transition-colors">Wishlists</a></li>
                <li><a href="/occasions" className="hover:text-white transition-colors">Occasions</a></li>
                <li><a href="/gift-concierge" className="hover:text-white transition-colors">Gift Concierge</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-accent-300">
                <li><a href="/about" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Returns</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-accent-700 mt-8 pt-8 text-center text-accent-400">
            <p>&copy; {new Date().getFullYear()} Giftsy. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProductDetailPage;